import {Injectable, inject} from "@angular/core";
import {Observable, firstValueFrom} from "rxjs";
import * as _ from "lodash";
import {TrainContainerDetails} from "../../models/train-container-details.model";
import {ThreePlExtraData} from "../../models/threepl-extra-data.model";
import {ThreePLProviderType} from "../../types/threePL-provider.type";
import {TrainVoucher} from "../models/train-voucher.model";
import {TrainProviderExternalData} from "../models/train-provider-settings.model";
import {ThreePLTrainAlertsService} from "./threepl-train-alerts.service";
import {ThreePLTrainHttpService} from "./threepl-train-http.service";
import {ILogService} from "angular";
import {UiRouterStateService} from "../../../commons/angular/models/ui-router-state.model";
import {BringozLoaderService} from "../../../commons/services/bringoz-loader.service";
import {SplitService} from "../../../commons/services/split-service.service";
import {FulfillmentStepRequestsService} from "../../../fulfillment/services/fulfillment-step-requests.service";
import {TRAIN_VOUCHER_CREATE_ERROR_ALERTS, VOUCHERS_PRINT_WINDOW_FEATURES} from "../constants/threepl-train.constant";

export interface DispatchedFulfillmentStep {
  id?: string;
  attempts: {referenceId: string}[];
}

interface DispatchConsolidateQuery {
  stepIds: string[];
  from: string;
  to: string;
  type: string;
  supplierAccountId: string;
  providerType: ThreePLProviderType;
}

interface DispatchError {
  code?: number;
}

export interface FulfillmentStepItem {
  barcode?: string;
  count?: number;
  [key: string]: unknown;
}

export interface FulfillmentStepForContainers {
  id: string;
  orderId?: string;
  items?: FulfillmentStepItem[];
  itemsExt?: FulfillmentStepItem[];
}

@Injectable({providedIn: "root"})
export class ThreePLTrainService {
  private readonly loader = inject<BringozLoaderService>("bringozLoaderService" as never);
  private readonly log = inject<ILogService>("$log" as never);
  private readonly fulfillmentStepRequestsService = inject<FulfillmentStepRequestsService>("fulfillmentStepRequestsService" as never);
  private readonly state = inject<UiRouterStateService>("$state" as never);
  private readonly splitService = inject<SplitService>("splitService" as never);
  private readonly http = inject(ThreePLTrainHttpService);
  private readonly alerts = inject(ThreePLTrainAlertsService);

  createVoucher(containerDetails: ThreePlExtraData, supplierAccountId: string, stepIds: string[], from: string, to: string): Promise<DispatchedFulfillmentStep[]> {
    this.loader.on();
    const query: DispatchConsolidateQuery = {
      stepIds, from, to, type: "ThirdParty", supplierAccountId, providerType: ThreePLProviderType.TrainIL,
    };
    return this.fulfillmentStepRequestsService.dispatchConsolidate(containerDetails, query)
      .catch((error: DispatchError) => {
        this.log.error(error);
        const alert = TRAIN_VOUCHER_CREATE_ERROR_ALERTS[error?.code] ?? "trainVoucherCreateError";
        (this.alerts[alert] as () => void).call(this.alerts);
        throw error;
      })
      .finally(() => this.loader.off());
  }

  updateService(voucherId: string, voucher: TrainVoucher): Promise<TrainVoucher> {
    return this.runVoucherAction(this.http.updateService(voucherId, voucher));
  }

  completeService(voucherId: string): Promise<TrainVoucher> {
    return this.runVoucherAction(this.http.completeService(voucherId));
  }

  cancelService(voucherId: string): Promise<TrainVoucher> {
    return this.runVoucherAction(this.http.cancelService(voucherId));
  }

  getTrainProviderExternalData(): Promise<TrainProviderExternalData> {
    this.loader.on();
    return firstValueFrom(this.http.getProviderExternalData())
      .catch((error: unknown) => {
        this.log.error(error);
        throw error;
      })
      .finally(() => this.loader.off());
  }

  getEmptyContainer(stepId?: string): TrainContainerDetails {
    return {
      omsOrderId: undefined, cargoType: undefined, stepId, item: {},
      isClosePacking: undefined, isMiddleLocation: undefined, isWeighted: undefined, isContentDeclared: undefined,
      materialOwnerContacts: undefined, materialPackagingContacts: undefined, containerType: undefined,
      unNumberInfo: undefined, materialInfo: undefined, isEmpty: undefined,
    };
  }

  getContainersBySteps(steps: FulfillmentStepForContainers[]): TrainContainerDetails[] {
    return _.flatMap(steps, (step) => {
      const items = step.itemsExt || step.items;
      return _.flatMap(items, (item) =>
        item.count > 1 ? _.times(item.count, () => this.toContainer(step, item)) : [this.toContainer(step, item)]);
    });
  }

  printVoucher(ids: string[]): void {
    const siteId = this.splitService.getSiteId();
    if (!siteId || !ids?.length) {
      return;
    }
    const url = this.state.href("print.vouchers", {ids, siteId});
    window.open(url, "_blank", VOUCHERS_PRINT_WINDOW_FEATURES);
  }

  private runVoucherAction(request: Observable<TrainVoucher>): Promise<TrainVoucher> {
    this.loader.on();
    return firstValueFrom(request)
      .then((voucher) => {
        this.alerts.trainVoucherUpdateSuccess();
        return voucher;
      })
      .catch((error: unknown) => {
        this.log.error(error);
        this.alerts.trainVoucherUpdateError();
        throw error;
      })
      .finally(() => this.loader.off());
  }

  private toContainer(step: FulfillmentStepForContainers, item: FulfillmentStepItem): TrainContainerDetails {
    return {
      id: item.barcode, omsOrderId: step.orderId, cargoType: undefined, unNumberInfo: undefined, materialInfo: undefined,
      stepId: step.id, item, isClosePacking: undefined, isMiddleLocation: undefined, isWeighted: undefined,
      isContentDeclared: undefined, materialOwnerContacts: undefined, materialPackagingContacts: undefined,
    };
  }
}
